import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import profile from "../utils/profile_data";
import { modelProfileActions, trackingService } from '../../../di/di';
import { useDispatch, useSelector } from "react-redux";
import { getAvatarProfile } from "../../../state/slice/model_profile_slice";
import ModelProfileLoadingComp from "../component/model_profile_loading_comp";
import { useNavigate } from "react-router-dom";
import { DashboardRoute, PageNotFound } from "../../../../../core/routes/route_constants";
// Create context
const ModelProfileContext = createContext();

// Provider component
export function ModelProfileContextProvider({ children, name }) {
  const [activeTab, setActiveTab] = useState("about");
  const [showConfetti, setShowConfetti] = useState(false);
  const [showNextStepModal, setShowNextStepModal] = useState(false);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(profile);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerImageIndex, setViewerImageIndex] = useState(0);
  const dispatch = useDispatch();
  const avatarProfile = useSelector(state => state.modelProfile.profile);
  const isFetching = useSelector(state => state.modelProfile.isFetching);
  const isError = useSelector(state => state.modelProfile.isError);
  const errorMessage = useSelector(state => state.modelProfile.errorMessage);
  const isAuthenticated = useSelector(state => state.modelProfile.isAuthenticated);
  const isPurchaseCompleted = useSelector(state => state.modelProfile.isPurchaseCompleted);
  const user = useSelector(state => state.user.user);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(modelProfileActions.resetIsError());
    dispatch(modelProfileActions.resetProfile());
    dispatch(getAvatarProfile({ avatarKey: name }));
    dispatch(modelProfileActions.setIsAuthenticated(user != null));
    dispatch(modelProfileActions.setIsPurchaseCompleted(user?.tierId != null));
    trackingService.trackEvent("model_profile_page_viewed", { avatar: name, timestamp: new Date().toISOString(), user_id: user?.id });
  }, [name, dispatch, user?.tierId]);
  

  useEffect(() => {
    // console.log("Avatar profile updated:", avatarProfile);
    if (avatarProfile) {
      const profileData = avatarProfile.data || avatarProfile;
      // console.log("Setting current profile with data:", profileData);
      setCurrentProfile(profileData);
    }
  }, [avatarProfile]);

  // Handle showing signup flow
  const handleStartSignupFlow = () => {
    if(isAuthenticated){
      window.location.href = DashboardRoute;
    } else {
      setShowNextStepModal(true);
    }
  };

  // Handle actual unlocking after signup
  const handleCompleteSignup = () => {
    setShowSignupForm(false);
    setShowConfetti(true);

    setTimeout(() => {
      setShowConfetti(false);
    }, 4000);
  };

  // Handle form submission
  const handleSubmit = (e, formData) => {
    e.preventDefault();
    console.log("Account created:", formData);
    handleCompleteSignup();
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowNextStepModal(false);
  };

  // Context value
  const value = {
    activeTab,
    setActiveTab,
    purchaseCompleted: isPurchaseCompleted,
    showConfetti,
    setShowConfetti,
    showNextStepModal,
    setShowNextStepModal,
    showSignupForm,
    setShowSignupForm,
    handleStartSignupFlow,
    handleCompleteSignup,
    handleSubmit,
    handleCloseModal,
    profile: currentProfile,
    isAuthenticated,
    isFetching,
    isError,
    errorMessage,
    showImageViewer,
    setShowImageViewer,
    viewerImageIndex,
    setViewerImageIndex,
  };

  // Show loading state while fetching
  if (isFetching) {
    return <ModelProfileLoadingComp />;
  }

  // Show error state if there's an error
  if (isError) {
    navigate(PageNotFound); // Navigate to 404 page if there's an error
    return (
      <div className="h-dvh flex flex-col items-center justify-center bg-gradient-to-b from-slate-950 via-fuchsia-950 to-gray-950 text-white">
        <div className="text-red-400 text-xl mb-4">Error loading profile</div>
        <div className="text-pink-300 mb-6">{errorMessage}</div>
        <button
          onClick={() => {
            dispatch(modelProfileActions.resetIsError());
            dispatch(getAvatarProfile(name));
          }}
          className="px-4 py-2 bg-red-900/50 hover:bg-red-800/50 rounded-full text-white transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <ModelProfileContext.Provider value={value}>
      {children}
    </ModelProfileContext.Provider>
  );
}

// Custom hook to use the context
export function useModelProfile() {
  const context = useContext(ModelProfileContext);
  if (context === undefined) {
    throw new Error(
      "useModelProfile must be used within a ModelProfileProvider"
    );
  }
  return context;
}
